import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Readiness = {
  mode?: string;
  webhook_secret_present?: boolean;
  account?: { id?: string; business_name?: string | null } | null;
};

/**
 * Persistent banner that confirms which Stripe mode the backend is using and
 * where the keys come from. Shown across all admin pages via AdminShell.
 */
export function StripeLiveModeBanner() {
  const [r, setR] = useState<Readiness | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    supabase.functions.invoke("stripe-readiness").then(({ data }) => {
      if (alive && data) setR(data as Readiness);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (dismissed || !r?.mode) return null;
  const isLive = r.mode === "live";
  const tone = isLive
    ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-200"
    : "border-amber-500/40 bg-amber-500/5 text-amber-200";

  return (
    <div className={`border ${tone} px-4 py-3 mb-4 text-xs font-body flex flex-wrap items-center gap-3`}>
      <ShieldCheck className="w-4 h-4 shrink-0" />
      <span>
        Stripe is in <strong className="font-display tracking-wider">{r.mode.toUpperCase()}</strong> mode
        {r.account?.business_name ? ` — ${r.account.business_name}` : ""}
        {r.account?.id ? ` (${r.account.id})` : ""}.
        Keys are read at runtime from backend secrets <code className="font-mono">STRIPE_SECRET_KEY</code> and{" "}
        <code className="font-mono">STRIPE_WEBHOOK_SECRET</code>
        {r.webhook_secret_present ? "" : " (webhook secret MISSING)"}.
      </span>
      <div className="ml-auto flex items-center gap-3">
        <Link to="/admin/stripe/webhook-status" className="underline inline-flex items-center gap-1">
          Webhook status <ExternalLink className="w-3 h-3" />
        </Link>
        <Link to="/admin/stripe/key-swap" className="underline inline-flex items-center gap-1">
          Rotate keys
        </Link>
        <button onClick={() => setDismissed(true)} className="opacity-60 hover:opacity-100">×</button>
      </div>
    </div>
  );
}